import {
  useState,
  ReactNode,
  createContext,
  useContext,
  isValidElement,
  useEffect,
  useLayoutEffect,
  CSSProperties,
  useRef,
  ReactElement,
  useMemo,
  useCallback,
  memo,
  cloneElement,
} from 'react';

import {
  useFloating,
  autoUpdate,
  offset,
  shift,
  flip,
  useClick,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
  useHover,
  safePolygon,
  FloatingFocusManager,
  size,
} from '@floating-ui/react';
import clsx from 'clsx';
import { List, type ListImperativeAPI, type RowComponentProps } from 'react-window';

import { sprinkles, tokens } from '../../tokens';
import { IconButton } from '../Button';
import { Checkbox } from '../Checkbox/Checkbox';
import { Divider, DividerProps } from '../Divider';
import { Flex } from '../Flex';
import { IconCheckLine, IconMoreHorizLine, IconKeyboardArrowRightLine } from '../Icon';
import { useModalContext } from '../Modal/ModalContext';
import { Scrollbar } from '../Scrollbar';
import {
  dropdownMenu,
  dropdownItem,
  dropdownItemDisabled,
  noScroll,
  dropdownItemActions,
  dropdownItemCheckbox,
  DROPDOWN_MENU_MAX_HEIGHT,
} from './Dropdown.css';
import type {
  DropdownItemDef,
  DropdownMenuItemType,
  DropdownMenuItemProps,
  DropdownItemClickHandler,
  DropdownSubMenuDef,
  VirtualizedOptions,
} from './types';
import { useDropdownKeyboardNavigation } from './useDropdownKeyboardNavigation';

// Dropdown Context
type DropdownKeyboardNavigationOptions = {
  useItemCountForNavigation: boolean;
  onNavigateToIndex?: (index: number) => void;
  itemKeys?: string[];
};

interface DropdownContextValue {
  menuRef: React.RefObject<HTMLDivElement>;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  refs: ReturnType<typeof useFloating>['refs'];
  floatingStyles: ReturnType<typeof useFloating>['floatingStyles'];
  getReferenceProps: ReturnType<typeof useInteractions>['getReferenceProps'];
  getFloatingProps: ReturnType<typeof useInteractions>['getFloatingProps'];
  context: ReturnType<typeof useFloating>['context'];
  registerKeyboardNavigation?: (options: DropdownKeyboardNavigationOptions) => void;
  maxHeight: number;
}

const DropdownContext = createContext<DropdownContextValue | null>(null);

const useDropdownContext = () => {
  const context = useContext(DropdownContext);
  if (!context) {
    throw new Error('Dropdown components must be used within DropdownRoot');
  }
  return context;
};

// DropdownRoot Component
export interface DropdownRootProps {
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  width?: CSSProperties['width'];
  maxHeight?: number;
  trigger?: 'click' | 'mousedown';
  /**
   * 메뉴와 트리거 사이의 offset
   * - number: 주축(상하) offset만 설정
   * - object: { mainAxis: 주축(상하), crossAxis: 교차축(좌우) }
   * @default 4
   */
  menuOffset?: number | { mainAxis?: number; crossAxis?: number };
  itemCount?: number;
  disableItemFocus?: boolean;
  /**
   * itemCount 기반 키보드 네비게이션 사용 여부 (가상화 리스트 대응)
   */
  useItemCountForNavigation?: boolean;
  /**
   * 인덱스 이동 시 스크롤 요청 콜백
   */
  onNavigateToIndex?: (index: number) => void;
}

export const DropdownRoot: React.FC<DropdownRootProps> = ({
  children,
  open,
  onOpenChange,
  width,
  maxHeight = DROPDOWN_MENU_MAX_HEIGHT,
  trigger = 'click',
  menuOffset = 4,
  itemCount,
  disableItemFocus,
  useItemCountForNavigation,
  onNavigateToIndex,
}) => {
  const [internalOpen, setInternalOpen] = useState(false);

  const isOpen = open ?? internalOpen;
  const setIsOpen = onOpenChange ?? setInternalOpen;
  const [registeredKeyboardNavigation, setRegisteredKeyboardNavigation] =
    useState<DropdownKeyboardNavigationOptions>({
      useItemCountForNavigation: false,
      itemKeys: undefined,
    });

  const registerKeyboardNavigation = useCallback((options: DropdownKeyboardNavigationOptions) => {
    setRegisteredKeyboardNavigation(options);
  }, []);

  const effectiveUseItemCountForNavigation =
    useItemCountForNavigation ?? registeredKeyboardNavigation.useItemCountForNavigation;
  const effectiveOnNavigateToIndex =
    onNavigateToIndex ?? registeredKeyboardNavigation.onNavigateToIndex;
  const effectiveItemKeys = registeredKeyboardNavigation.itemKeys;

  const { refs, floatingStyles, context } = useFloating({
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: 'bottom-start',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(menuOffset),
      flip({
        padding: 8,
      }),
      shift({
        padding: 8,
        crossAxis: true,
      }),
      size({
        apply({ rects, elements }) {
          Object.assign(elements.floating.style, {
            width: width
              ? typeof width === 'number'
                ? `${width}px`
                : width
              : `${rects.reference.width}px`,
          });
        },
      }),
    ],
  });

  const click = useClick(context, { event: trigger });
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'menu' });

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss, role]);

  // 키보드 네비게이션 훅 사용
  const { menuRef, handleKeyDown, handleMouseOver } = useDropdownKeyboardNavigation({
    isOpen,
    itemCount,
    disableItemFocus,
    useItemCountForNavigation: effectiveUseItemCountForNavigation,
    onNavigateToIndex: effectiveOnNavigateToIndex,
    itemKeys: effectiveItemKeys,
  });

  return (
    <DropdownContext.Provider
      value={{
        menuRef,
        isOpen,
        setIsOpen,
        refs,
        floatingStyles,
        getReferenceProps,
        getFloatingProps,
        context,
        registerKeyboardNavigation,
        maxHeight,
      }}
    >
      <div onKeyDown={handleKeyDown} onMouseOver={handleMouseOver} style={{ display: 'contents' }}>
        {children}
      </div>
    </DropdownContext.Provider>
  );
};

// DropdownTrigger Component
export interface DropdownTriggerProps {
  children: ReactElement | ReactNode;
  disabled?: boolean;
  readOnly?: boolean;
  /**
   * true(기본값): children에 직접 ref/props를 주입 (cloneElement)
   * false: 내부 span wrapper를 렌더링하고 거기에 ref/props를 연결
   */
  asChild?: boolean;
}

export const DropdownTrigger: React.FC<DropdownTriggerProps> = ({
  children,
  disabled = false,
  readOnly = false,
  asChild = true,
}) => {
  const { refs, getReferenceProps, setIsOpen, isOpen } = useDropdownContext();

  const handleKeyDown = (e: React.KeyboardEvent<HTMLElement>) => {
    if (disabled || readOnly) return;
    // Enter 키로 드롭다운 토글
    if (e.key === 'Enter' && !isOpen) {
      e.preventDefault();
      setIsOpen(true);
    }
  };

  const referenceProps = disabled || readOnly ? {} : getReferenceProps();

  // asChild={false}: wrapper span을 렌더링
  if (!asChild) {
    return (
      <span
        ref={refs.setReference}
        {...referenceProps}
        onKeyDown={handleKeyDown}
        style={{ display: 'inline-block', width: '100%' }}
      >
        {children}
      </span>
    );
  }

  // asChild={true}: children에 직접 ref/props 주입
  if (!isValidElement(children)) {
    return children;
  }

  return cloneElement(children as ReactElement, {
    ref: refs.setReference,
    ...referenceProps,
    onKeyDown: handleKeyDown,
    ...(disabled ? { disabled: true } : {}),
  });
};

// DropdownMenu Component
export interface DropdownMenuProps {
  children: ReactNode;
  borderRadius?: number;
  preventScroll?: boolean;
  zIndex?: number;
  disableFocusTrap?: boolean;
}

export const DropdownMenu: React.FC<DropdownMenuProps> = ({
  children,
  borderRadius = 16,
  preventScroll = true,
  zIndex,
  disableFocusTrap = false,
}) => {
  const { isOpen, refs, floatingStyles, getFloatingProps, context, menuRef, maxHeight } =
    useDropdownContext();
  const modalContext = useModalContext();

  // Modal 내부에 있으면 Modal보다 높은 z-index를 자동으로 설정
  const effectiveZIndex = zIndex ?? (modalContext ? modalContext.zIndex + 10 : undefined);

  // 스크롤 방지
  useEffect(() => {
    if (!preventScroll) return;

    if (isOpen) {
      document.body.classList.add(noScroll);
    } else {
      document.body.classList.remove(noScroll);
    }

    return () => {
      document.body.classList.remove(noScroll);
    };
  }, [isOpen, preventScroll]);

  if (!isOpen) {
    return null;
  }

  return (
    <FloatingPortal>
      <FloatingFocusManager context={context} modal={true} disabled={disableFocusTrap}>
        <div
          ref={(node) => {
            refs.setFloating(node);
            (menuRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
          }}
          {...getFloatingProps()}
          className={clsx(dropdownMenu, preventScroll && noScroll)}
          style={{ borderRadius, zIndex: effectiveZIndex, ...floatingStyles, maxHeight }}
        >
          {children}
        </div>
      </FloatingFocusManager>
    </FloatingPortal>
  );
};

// DropdownMenuItem Component
export const DropdownMenuItem: React.FC<DropdownMenuItemProps> = ({
  children,
  disabled = false,
  selected = false,
  onClick,
  selectable = 'none',
  preventClose = false,
  preventClick = false,
  noStyle = false,
  actions,
  style,
  className,
  ...restProps
}) => {
  const { setIsOpen } = useDropdownContext();
  const [actionsOpen, setActionsOpen] = useState(false);

  const handleClick = (
    e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>,
  ) => {
    if (disabled || preventClick) return;
    onClick?.(e);
    if (selectable !== 'multi' && !preventClose) {
      setIsOpen(false);
    }
  };

  const actionsMenu = useMemo(() => {
    return (
      actions?.map((action) =>
        action.type === 'item'
          ? {
              ...action,
              onClick: (params: Parameters<DropdownItemClickHandler>[0]) => {
                if (action.disabled) {
                  return;
                }
                action.onClick?.(params);
                setActionsOpen(false);
                setIsOpen(false);
              },
            }
          : action,
      ) ?? []
    );
  }, [actions, setIsOpen]);

  const renderContent = () => {
    switch (selectable) {
      case 'multi':
        return (
          <Flex align="center" gap={8} className={sprinkles({ flex: 1, minWidth: 0 })}>
            <Checkbox checked={selected} disabled={disabled} className={dropdownItemCheckbox} />
            <div className={sprinkles({ flex: 1, minWidth: 0 })}>{children}</div>
          </Flex>
        );
      case 'single':
        return (
          <Flex align="center" justify="between" gap={8} className={sprinkles({ flex: 1 })}>
            <div>{children}</div>
            {selected && (
              <IconCheckLine
                width={20}
                height={20}
                fill={disabled ? tokens.color.grayOpacity22 : tokens.color.gray900}
              />
            )}
          </Flex>
        );
      case 'none':
        return <div style={{ flex: 1 }}>{children}</div>;
    }
  };

  return (
    <div
      className={
        noStyle
          ? className
          : clsx(
              dropdownItem({ selectable, disabled }),
              disabled && dropdownItemDisabled,
              className,
            )
      }
      onClick={handleClick}
      role="menuitem"
      aria-disabled={disabled}
      tabIndex={-1}
      style={style}
      {...restProps}
    >
      {renderContent()}
      {actions && actions.length > 0 && !disabled && (
        <div className={dropdownItemActions} onClick={(e) => e.stopPropagation()}>
          <Dropdown
            open={actionsOpen}
            onOpenChange={setActionsOpen}
            width="fit-content"
            menu={{
              items: actionsMenu,
            }}
          >
            <IconButton
              buttonType="ghost"
              buttonSize="xs"
              icon={<IconMoreHorizLine />}
              tooltipMessage="더보기"
            />
          </Dropdown>
        </div>
      )}
    </div>
  );
};

// DropdownSubMenu Component
interface DropdownSubMenuProps {
  subMenu: DropdownSubMenuDef;
  dataIndex?: number;
}

export const DropdownSubMenu: React.FC<DropdownSubMenuProps> = ({ subMenu, dataIndex }) => {
  const { setIsOpen: setParentOpen } = useDropdownContext();
  const [subMenuOpen, setSubMenuOpen] = useState(false);
  const triggerRef = useRef<HTMLDivElement>(null);

  // SubMenu floating UI
  const {
    refs: subMenuRefs,
    floatingStyles: subMenuFloatingStyles,
    context: subMenuContext,
  } = useFloating({
    open: subMenuOpen,
    onOpenChange: setSubMenuOpen,
    placement: 'right-start',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(4),
      flip({
        padding: 8,
      }),
      shift({
        padding: 8,
      }),
    ],
  });

  const subMenuHover = useHover(subMenuContext, {
    handleClose: safePolygon(),
  });

  const { getReferenceProps: getSubMenuReferenceProps, getFloatingProps: getSubMenuFloatingProps } =
    useInteractions([subMenuHover]);

  // 서브메뉴 키보드 네비게이션
  const {
    menuRef: subMenuRef,
    handleKeyDown: handleSubMenuKeyDown,
    handleMouseOver: handleSubMenuMouseOver,
  } = useDropdownKeyboardNavigation({
    isOpen: subMenuOpen,
    onSubMenuClose: () => {
      setSubMenuOpen(false);
      // 서브메뉴가 닫힐 때 트리거 아이템으로 포커스 복귀
      triggerRef.current?.focus();
    },
  });

  const handleSubMenuItemClick = (
    e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>,
    item: DropdownItemDef,
  ) => {
    if (item.disabled) return;
    item.onClick?.({ event: e, item });
    subMenu.onItemClick?.({ event: e, item });
    // multi-select 모드에서는 메뉴를 닫지 않음
    if (subMenu.selectable !== 'multi') {
      setSubMenuOpen(false);
      setParentOpen(false);
    }
  };

  // 트리거 아이템의 키보드 이벤트 핸들러
  const handleTriggerKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (subMenu.disabled) return;

    if (e.key === 'ArrowRight') {
      e.preventDefault();
      e.stopPropagation();
      setSubMenuOpen(true);
    }
  };

  return (
    <>
      <div
        ref={(node) => {
          subMenuRefs.setReference(node);
          (triggerRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }}
        {...getSubMenuReferenceProps()}
        className={clsx(
          dropdownItem({
            selectable: 'none',
            disabled: subMenu.disabled,
          }),
          subMenu.disabled && dropdownItemDisabled,
        )}
        role="menuitem"
        aria-disabled={subMenu.disabled}
        aria-haspopup="menu"
        aria-expanded={subMenuOpen}
        data-index={dataIndex}
        tabIndex={-1}
        onKeyDown={handleTriggerKeyDown}
      >
        <Flex align="center" justify="between" gap={8} className={sprinkles({ flex: 1 })}>
          <div className={sprinkles({ flex: 1 })}>{subMenu.label}</div>
          <IconKeyboardArrowRightLine
            width={20}
            height={20}
            fill={subMenu.disabled ? tokens.color.grayOpacity22 : tokens.color.gray700}
          />
        </Flex>
      </div>

      {/* SubMenu Portal */}
      {subMenuOpen && !subMenu.disabled && (
        <FloatingPortal>
          <div
            ref={(node) => {
              subMenuRefs.setFloating(node);
              (subMenuRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
            }}
            {...getSubMenuFloatingProps()}
            className={dropdownMenu}
            style={{
              ...subMenuFloatingStyles,
              width: 'fit-content',
              minWidth: 120,
              borderRadius: 16,
            }}
            onKeyDown={handleSubMenuKeyDown}
            onMouseOver={handleSubMenuMouseOver}
          >
            <DropdownMenuContent
              items={subMenu.items}
              selectedKeys={subMenu.selectedKeys}
              selectable={subMenu.selectable}
              onItemClick={handleSubMenuItemClick}
            />
          </div>
        </FloatingPortal>
      )}
    </>
  );
};

// DropdownDivider Component
export const DropdownMenuDivider: React.FC<DividerProps> = (props) => {
  return <Divider orientation="horizontal" thickness={2} style={{ margin: '0 -6px' }} {...props} />;
};

const DEFAULT_VIRTUALIZED_ITEM_HEIGHT = 32;
const DEFAULT_VIRTUALIZED_OVERSCAN_COUNT = 4;
const DROPDOWN_MENU_VERTICAL_PADDING = 12;

type DropdownMenuVirtualizedItemData = {
  items: DropdownMenuItemType[];
  selectedKeys?: string[];
  selectable?: 'single' | 'multi' | 'none';
  onItemClick?: (
    e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>,
    item: DropdownItemDef,
  ) => void;
  enableAutoRowHeight: boolean;
  setRowHeight?: (key: string, height: number) => void;
};

const DropdownMenuVirtualizedRow = memo(
  ({
    index,
    style,
    selectedKeys,
    setRowHeight,
    enableAutoRowHeight,
    ...data
  }: RowComponentProps<DropdownMenuVirtualizedItemData>) => {
    const item = data.items[index];
    const rowContentRef = useRef<HTMLDivElement>(null);

    useLayoutEffect(() => {
      if (!enableAutoRowHeight || !setRowHeight || !item) return;
      const node = rowContentRef.current;
      if (!node) return;

      const measure = () => {
        const nextHeight = Math.ceil(node.getBoundingClientRect().height);
        if (nextHeight > 0) {
          setRowHeight(item.key, nextHeight);
        }
      };

      measure();

      if (typeof ResizeObserver === 'undefined') return;

      const observer = new ResizeObserver(() => {
        measure();
      });

      observer.observe(node);
      return () => observer.disconnect();
    }, [enableAutoRowHeight, setRowHeight, item]);

    if (!item) return null;

    const rowStyle = { ...style, width: '100%' };

    if (item.type === 'divider') {
      return (
        <div style={rowStyle}>
          <div ref={rowContentRef} style={{ width: '100%' }}>
            <DropdownMenuDivider {...item.dividerProps} />
          </div>
        </div>
      );
    }

    if (item.type === 'subMenu') {
      return (
        <div style={rowStyle}>
          <div ref={rowContentRef} style={{ width: '100%' }}>
            <DropdownSubMenu subMenu={item} dataIndex={index} />
          </div>
        </div>
      );
    }

    const isSelected = selectedKeys?.includes(item.key);
    const { key: _key, label, type: _type, onClick: _onClick, actions, ...itemProps } = item;

    return (
      <div style={rowStyle}>
        <div ref={rowContentRef} style={{ width: '100%' }}>
          <DropdownMenuItem
            data-index={index}
            selected={isSelected}
            selectable={data.selectable}
            onClick={(e) => data.onItemClick?.(e, item)}
            actions={actions}
            {...itemProps}
          >
            {label}
          </DropdownMenuItem>
        </div>
      </div>
    );
  },
);

DropdownMenuVirtualizedRow.displayName = 'DropdownMenuVirtualizedRow';

// DropdownMenuContent Component - items 렌더링 전담
export interface DropdownMenuContentProps {
  items: DropdownMenuItemType[];
  selectedKeys?: string[];
  selectable?: 'single' | 'multi' | 'none';
  onItemClick?: (
    e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>,
    item: DropdownItemDef,
  ) => void;
  /**
   * 스크롤 가상화 옵션
   */
  virtualized?: boolean | VirtualizedOptions;
}

export const DropdownMenuContent: React.FC<DropdownMenuContentProps> = ({
  items,
  selectedKeys,
  selectable,
  onItemClick,
  virtualized = false,
}) => {
  const { registerKeyboardNavigation, maxHeight } = useDropdownContext();
  const listRef = useRef<ListImperativeAPI>(null);
  const virtualizedOptions = typeof virtualized === 'object' ? (virtualized.options ?? {}) : {};
  const isVirtualized = Boolean(virtualized);
  const itemHeightOption = virtualizedOptions.itemHeight ?? 'auto';
  const enableAutoRowHeight = isVirtualized && itemHeightOption === 'auto';
  const itemKeys = useMemo(() => items.map((item) => item.key), [items]);

  const resolvedVirtualizedItemHeight =
    typeof itemHeightOption === 'number' ? itemHeightOption : DEFAULT_VIRTUALIZED_ITEM_HEIGHT;
  const resolvedVirtualizedMaxHeight =
    virtualizedOptions.maxHeight ?? maxHeight - DROPDOWN_MENU_VERTICAL_PADDING;
  const resolvedVirtualizedOverscanCount =
    virtualizedOptions.overscanCount ?? DEFAULT_VIRTUALIZED_OVERSCAN_COUNT;

  const [rowHeights, setRowHeights] = useState<Map<string, number>>(() => new Map());

  const setRowHeight = useCallback((key: string, height: number) => {
    setRowHeights((prev) => {
      const nextHeight = Math.ceil(height);
      if (prev.get(key) === nextHeight) {
        return prev;
      }
      const next = new Map(prev);
      next.set(key, nextHeight);
      return next;
    });
  }, []);

  const getResolvedItemHeight = useCallback(
    (item: DropdownMenuItemType | undefined) => {
      if (!enableAutoRowHeight || !item) return resolvedVirtualizedItemHeight;
      return rowHeights.get(item.key) ?? resolvedVirtualizedItemHeight;
    },
    [enableAutoRowHeight, resolvedVirtualizedItemHeight, rowHeights],
  );

  const handleNavigateToIndex = useCallback(
    (index: number) => {
      listRef.current?.scrollToRow({ index, align: 'auto', behavior: 'instant' });
    },
    [listRef],
  );

  const rowHeight = useCallback(
    (index: number, rowProps: DropdownMenuVirtualizedItemData) =>
      getResolvedItemHeight(rowProps.items[index]),
    [getResolvedItemHeight],
  );

  const listHeight = useMemo(() => {
    if (!isVirtualized) return 0;

    if (!enableAutoRowHeight) {
      return Math.min(items.length * resolvedVirtualizedItemHeight, resolvedVirtualizedMaxHeight);
    }

    if (items.length === 0) return 0;

    let totalHeight = 0;
    for (const item of items) {
      totalHeight += getResolvedItemHeight(item);
      if (totalHeight >= resolvedVirtualizedMaxHeight) {
        return resolvedVirtualizedMaxHeight;
      }
    }

    return Math.min(totalHeight, resolvedVirtualizedMaxHeight);
  }, [
    enableAutoRowHeight,
    getResolvedItemHeight,
    isVirtualized,
    items,
    resolvedVirtualizedItemHeight,
    resolvedVirtualizedMaxHeight,
  ]);

  const virtualizedItemData = useMemo<DropdownMenuVirtualizedItemData>(
    () => ({
      items,
      selectedKeys,
      selectable,
      onItemClick,
      enableAutoRowHeight,
      setRowHeight: enableAutoRowHeight ? setRowHeight : undefined,
    }),
    [items, selectedKeys, selectable, onItemClick, enableAutoRowHeight, setRowHeight],
  );

  useEffect(() => {
    if (!registerKeyboardNavigation || !isVirtualized) return;

    registerKeyboardNavigation({
      useItemCountForNavigation: true,
      onNavigateToIndex: handleNavigateToIndex,
      itemKeys,
    });

    return () => {
      registerKeyboardNavigation({
        useItemCountForNavigation: false,
        onNavigateToIndex: undefined,
        itemKeys: undefined,
      });
    };
  }, [handleNavigateToIndex, isVirtualized, itemKeys, registerKeyboardNavigation]);

  if (isVirtualized) {
    return (
      <List
        rowComponent={DropdownMenuVirtualizedRow}
        rowCount={items.length}
        rowHeight={enableAutoRowHeight ? rowHeight : resolvedVirtualizedItemHeight}
        rowProps={virtualizedItemData}
        overscanCount={resolvedVirtualizedOverscanCount}
        defaultHeight={listHeight}
        listRef={listRef}
        style={{ height: listHeight, width: '100%', overflowX: 'hidden' }}
      />
    );
  }

  return (
    <Scrollbar
      translateContentSizeYToHolder
      noScrollX
      style={{ height: '100%' }}
      scrollbarStyle={{ paddingRight: 0 }}
      trackYOffset={{ right: -6 }}
      contentProps={{
        style: { display: 'flex', flexDirection: 'column' },
      }}
    >
      {items.map((item, index) => {
        if (item.type === 'divider') {
          return <DropdownMenuDivider key={item.key} {...item.dividerProps} />;
        }

        if (item.type === 'subMenu') {
          return <DropdownSubMenu key={item.key} subMenu={item} dataIndex={index} />;
        }

        const isSelected = selectedKeys?.includes(item.key);
        const { key, label, type: _, onClick: __, actions, ...itemProps } = item;

        return (
          <DropdownMenuItem
            key={key}
            data-index={index}
            selected={isSelected}
            selectable={selectable}
            onClick={(e) => onItemClick?.(e, item)}
            actions={actions}
            {...itemProps}
          >
            {label}
          </DropdownMenuItem>
        );
      })}
    </Scrollbar>
  );
};

// Main Dropdown Component (Composition wrapper)
export interface DropdownProps
  extends
    Pick<DropdownRootProps, 'menuOffset' | 'open' | 'onOpenChange' | 'trigger' | 'width'>,
    Pick<DropdownMenuProps, 'preventScroll' | 'zIndex'> {
  children: ReactElement;
  selectable?: 'single' | 'multi';
  disabled?: boolean;
  readOnly?: boolean;
  menu: {
    items: DropdownMenuItemType[];
    selectedKeys?: string[];
    onClick?: DropdownItemClickHandler;
  };
}

export const Dropdown: React.FC<DropdownProps> = ({
  children,
  menu,
  selectable,
  preventScroll = true,
  zIndex,
  disabled = false,
  readOnly = false,
  ...dropdownRootProps
}) => {
  const handleItemClick = (
    e: React.MouseEvent<HTMLDivElement> | React.KeyboardEvent<HTMLDivElement>,
    item: DropdownItemDef,
  ) => {
    if (disabled) return;
    item.onClick?.({ event: e, item });
    menu.onClick?.({ event: e, item });
  };

  return (
    <DropdownRoot itemCount={menu.items.length} {...dropdownRootProps}>
      <DropdownTrigger disabled={disabled} readOnly={readOnly}>
        {children}
      </DropdownTrigger>
      <DropdownMenu preventScroll={preventScroll} zIndex={zIndex}>
        <DropdownMenuContent
          items={menu.items}
          selectedKeys={menu.selectedKeys}
          selectable={selectable}
          onItemClick={handleItemClick}
        />
      </DropdownMenu>
    </DropdownRoot>
  );
};
